import React, {ReactElement, useCallback, useMemo} from 'react';
import {
  View,
  StyleSheet,
  LayoutAnimation,
  ViewStyle,
  StyleProp,
} from 'react-native';
import Text from '@/components/basic/text';
import LinearGradient from '@/components/basic/linear-gradient';
import theme from '@/style';
import {capitalizeWords, cn, formatIndiaDateTime, toPriceStr} from '@/utils';
import globalStore from '@/services/global.state';
import LazyImage from '@components/basic/image';
import {NativeTouchableOpacity} from '@/components/basic/touchable-opacity';
import {useTranslation} from 'react-i18next';
const copyIcon = require('@components/assets/icons/bets/copy.webp');
import Clipboard from '@react-native-clipboard/clipboard';

interface moreDataType {
  key: string;
  value: string | ReactElement<{}>;
}

export interface RecordItemType {
  status?: 'PROCESS' | 'COMPLETE' | 'FAILED' | 'REVIEW';
  statusTip?: string;
  iconUrl: string | number;
  type?: number;
  typeName?: string;
  hasMore?: boolean;
  hasAmountPrefix?: boolean;
  isIncome?: boolean;
  showBalance?: boolean;
  hasStatus?: boolean;
  time?: string | number | Date;
  amount?: number;
  balance?: number;
  moreData?: moreDataType[];
  style?: StyleProp<ViewStyle>;
  consumeType?: number;
  gameName?: string;
  orderNum?: string;
  hasRiskStatus?: boolean;
  riskStatus?: string;
}

const COLORS_MAP = {
  PROCESS: {
    icon: null,
    text: 'In progress...',
    bg: ['rgba(23, 118, 255, 0.08)', 'rgba(23, 118, 255, 0)'],
    color: '#1776FF',
  },
  COMPLETE: {
    icon: require('../../assets/icons/records-list-item/complete.webp'),
    text: 'COMPLETE',
    bg: ['rgba(2.51, 200.81, 58.03, 0.08)', 'rgba(2.51, 200.81, 58.03, 0)'],
    color: '#009919',
  },
  REVIEW: {
    icon: null,
    text: 'In review...',
    bg: ['rgba(23, 118, 255, 0.08)', 'rgba(23, 118, 255, 0)'],
    color: '#1776FF',
  },
  FAILED: {
    icon: require('../../assets/icons/records-list-item/failed.webp'),
    text: 'failed',
    bg: ['rgba(200.81, 2.51, 2.51, 0.08)', 'rgba(200.81, 2.51, 2.51, 0)'],
    color: '#E20000',
  },
};

const RecordItem = (props: RecordItemType) => {
  const {
    type = 0,
    isIncome,
    amount = 0,
    status = 'PROCESS',
    balance = 0,
    time,
    hasMore = true,
    showBalance = true,
    statusTip,
    iconUrl,
    hasStatus = false,
    hasAmountPrefix,
    moreData = [],
    style = {},
    typeName = '',
    consumeType,
    gameName,
    orderNum,
    hasRiskStatus = false,
    riskStatus = '',
  } = props;
  const {i18n} = useTranslation();
  const isSpecial = React.useMemo(() => {
    return [0, 1, 3, 5, 20].includes(type);
  }, [type]);

  const riskStatusOption = useMemo(() => {
    const riskStyleMap: Record<
      string,
      {LinearGradient: string; text: string; textClass: string}
    > = {
      PROCESSING: {
        LinearGradient:
          'linear-gradient(90deg, rgba(23, 118, 255, 0.08) 0.04%, rgba(23, 118, 255, 0.00) 99.94%)',
        text: i18n.t('label.processing'),
        textClass: 'text-[#1776FF]',
      },
      UNISSUED: {
        LinearGradient:
          'linear-gradient(90deg, rgba(201, 3, 3, 0.08) 0.04%, rgba(201, 3, 3, 0.00) 99.94%)',
        text: i18n.t('label.unissued'),
        textClass: 'text-[#E20000]',
      },
    };
    return riskStyleMap[riskStatus] || null;
  }, [riskStatus, i18n]);

  const [showDetail, setShowDetail] = React.useState(false);

  const arrowStyle = React.useMemo(() => {
    return {
      transform: [
        {
          rotate: showDetail ? '180deg' : '0deg',
        },
      ],
    };
  }, [showDetail]);

  const amountColor = React.useMemo(() => {
    if (hasStatus) {
      if (status === 'REVIEW' || status === 'FAILED') {
        return theme.fontColor.secAccent;
      }
    }
    return theme.basicColor.dark;
  }, [hasStatus, status]);

  const timeLabel = React.useMemo(() => {
    if (type === 999) {
      return i18n.t(`label.${typeName}`);
    }
    let typeT = 'recharge';
    if (isSpecial) {
      if (type === 3) {
        typeT = 'withdraw';
      }
      if ([0, 20].includes(type)) {
        typeT = 'transfer';
      }
    } else {
      typeT = 'order';
    }
    const t = `label.${typeT}`;
    return i18n.t(t);
  }, [type, isSpecial, i18n, typeName]);

  const handleCopy = useCallback(() => {
    if (!orderNum) {
      return;
    }
    Clipboard.setString(orderNum);
    globalStore.globalSucessTotal(i18n.t('copy-success'));
  }, [orderNum, i18n]);

  return (
    <View
      style={[
        theme.borderRadius.m,
        theme.margin.btml,
        theme.background.white,
        theme.padding.tbs,
        style,
      ]}>
      {hasStatus && (
        <LinearGradient
          style={[
            theme.flex.row,
            theme.flex.between,
            theme.padding.lrl,
            theme.margin.lrl,
            theme.padding.tbs,
            styles.progress,
          ]}
          colors={COLORS_MAP[status].bg}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 0}}>
          <View style={[theme.flex.row, theme.flex.centerByCol]}>
            {COLORS_MAP[status].icon && (
              <View style={[theme.margin.rightxxs]}>
                <LazyImage
                  imageUrl={COLORS_MAP[status].icon}
                  width={12}
                  height={12}
                  occupancy="transparent"
                />
              </View>
            )}
            <Text color={COLORS_MAP[status].color}>
              {i18n.t(`records.status.${status}`).toUpperCase()}
            </Text>
          </View>
          <Text
            color={
              status === 'FAILED'
                ? COLORS_MAP[status].color
                : theme.fontColor.accent
            }>
            {statusTip}
          </Text>
        </LinearGradient>
      )}
      {hasRiskStatus && riskStatusOption && (
        <div
          className={cn(
            'h-7 w-full rounded-l-full text-xs uppercase mx-[12px] px-3 flex flex-row items-center',
            riskStatusOption.textClass,
          )}
          style={{
            backgroundImage: riskStatusOption.LinearGradient,
          }}>
          {riskStatusOption.text}
        </div>
      )}
      <View style={styles.item}>
        <View
          style={[
            theme.flex.row,
            theme.flex.centerByCol,
            theme.margin.rightxl,
            theme.flex.flex1,
          ]}>
          <LazyImage
            imageUrl={iconUrl}
            width={24}
            height={24}
            occupancy="transparent"
          />
          <View style={[theme.flex.flex1]}>
            <Text
              numberOfLines={1}
              style={[theme.margin.leftl]}
              size="medium"
              fontFamily="fontInterBold">
              {capitalizeWords(typeName)}
            </Text>
          </View>
        </View>
        <NativeTouchableOpacity
          disabled={!hasMore}
          onPress={() => {
            LayoutAnimation.configureNext(
              LayoutAnimation.Presets.easeInEaseOut,
            );
            setShowDetail(!showDetail);
          }}
          style={[theme.flex.row, theme.flex.centerByCol]}>
          <Text style={[theme.margin.rights]}>
            {hasAmountPrefix && (
              <Text blod size="large" color={theme.basicColor.dark}>
                {isIncome ? '+ ' : '- '}
              </Text>
            )}
            <Text blod color={amountColor} size="large">
              {toPriceStr(amount, {
                thousands: true,
                spacing: true,
                currency: globalStore.currency,
              })}
            </Text>
            <Text />
          </Text>
          {hasMore && (
            <View style={arrowStyle}>
              <LazyImage
                width={14}
                height={14}
                occupancy="transparent"
                imageUrl={require('@/components/assets/icons/records-list-item/arrow-down.webp')}
              />
            </View>
          )}
        </NativeTouchableOpacity>
      </View>

      <View style={styles.item}>
        <Text color={theme.fontColor.accent}>{i18n.t('label.walletType')}</Text>
        <Text>{consumeType === 3 ? 'Gaming Wallet' : 'Primary Wallet'}</Text>
      </View>

      {showBalance && (
        <View style={styles.item}>
          <Text color={theme.fontColor.accent}>{i18n.t('label.balance')}</Text>
          <Text>
            {toPriceStr(balance, {
              thousands: true,
              spacing: true,
              currency: globalStore.currency,
            })}
          </Text>
        </View>
      )}
      <View style={styles.item}>
        <Text color={theme.fontColor.accent}>
          {timeLabel} {i18n.t('label.time')}
        </Text>
        <Text>{formatIndiaDateTime(time)}</Text>
      </View>
      {typeName !== 'transfer' && (
        <View style={styles.item}>
          <Text color={theme.fontColor.accent}>
            {i18n.t('records.label.orderNum')}
          </Text>
          <View style={[theme.flex.row, theme.flex.centerByCol, {gap: 4}]}>
            <Text>{orderNum}</Text>
            <NativeTouchableOpacity
              onPress={handleCopy}
              style={[theme.margin.lefts]}>
              <LazyImage
                imageUrl={copyIcon}
                width={14}
                height={14}
                occupancy="transparent"
              />
            </NativeTouchableOpacity>
          </View>
        </View>
      )}
      {showDetail && (
        <View>
          {moreData.map((item, index) => (
            <View style={styles.item} key={index}>
              <Text color={theme.fontColor.accent}>{item.key}</Text>
              {React.isValidElement(item.value) ? (
                item.value
              ) : (
                <Text>{item.value}</Text>
              )}
            </View>
          ))}
        </View>
      )}
      {showDetail && typeName === 'Bets' && (
        <View>
          {!moreData.some(
            (v: moreDataType) => v.key === i18n.t('records.label.game'),
          ) &&
            gameName && (
              <View style={styles.item}>
                <Text color={theme.fontColor.accent}>
                  {i18n.t('records.label.game')}
                </Text>
                <Text>{gameName}</Text>
              </View>
            )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  item: {
    height: 40,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  progress: {
    borderRadius: 20,
  },
});

export default RecordItem;
